import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button, Input } from '../../components/common'
import { useAuthStore } from '../../store/auth.store'

export const ForgotPasswordForm: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { forgotPassword, error, isLoading, clearError } = useAuthStore()
  const [email, setEmail] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!email) {
      newErrors.email = t('validation.emailRequired')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t('validation.emailInvalid')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      await forgotPassword(email)
      navigate('/reset-password')
      setIsSubmitted(true)
    } catch (err) {
      // Error is handled by the store
    }
  }

  const handleBackToLogin = () => {
    navigate('/login')
  }

  const handleTryAgain = () => {
    setIsSubmitted(false)
    setEmail('')
    clearError()
  }

  if (isSubmitted) {
    return (
      <div className="auth-form">
        <h2 className="auth-form__title">{t('auth.resetPassword')}</h2>

        <div className="auth-form__success" role="alert">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="icon icon-tabler icon-tabler-circle-check"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            strokeWidth="2"
            stroke="currentColor"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ marginRight: '8px', verticalAlign: 'middle' }}
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <circle cx="12" cy="12" r="9" />
            <path d="M9 12l2 2l4 -4" />
          </svg>
          {t('auth.resetCodeSent', { email })}
        </div>

        <div className="auth-form__info">
          <p>{t('auth.checkEmailForCode')}</p>
          <p style={{ marginTop: '8px', fontSize: '0.85rem' }}>
            <strong>{t('auth.note')}:</strong> {t('auth.codeExpiresIn')}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
          <Button onClick={handleBackToLogin} variant="secondary" fullWidth>
            {t('auth.backToLogin')}
          </Button>
          <Button
            onClick={() =>
              navigate(`/reset-password?email=${encodeURIComponent(email)}`)
            }
            variant="primary"
            fullWidth
          >
            {t('auth.enterResetCode')}
          </Button>
        </div>

        <p className="auth-form__footer">
          {t('auth.didntReceiveCode')}{' '}
          <button
            type="button"
            className="auth-form__link-button"
            onClick={handleTryAgain}
          >
            {t('auth.sendAgain')}
          </button>
        </p>
      </div>
    )
  }

  return (
    <form className="auth-form" onSubmit={handleSubmit}>
      <h2 className="auth-form__title">{t('auth.resetPassword')}</h2>

      <p className="auth-form__description">{t('auth.enterEmailForReset')}</p>

      {error && (
        <div className="auth-form__error" role="alert">
          {error}
        </div>
      )}

      <Input
        label={t('auth.email')}
        type="email"
        name="email"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value)
          if (errors.email) setErrors({ ...errors, email: '' })
          if (error) clearError()
        }}
        error={errors.email}
        placeholder={t('auth.enterEmail')}
        autoComplete="email"
        required
        autoFocus
      />

      <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
        <Button
          type="button"
          onClick={handleBackToLogin}
          variant="secondary"
          fullWidth
        >
          {t('common.back')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={isLoading}
          disabled={isLoading}
          fullWidth
        >
          {isLoading ? t('auth.sending') : t('auth.sendResetCode')}
        </Button>
      </div>

      <p className="auth-form__footer">
        {t('auth.rememberPassword')}{' '}
        <a href="/login" className="auth-form__link">
          {t('auth.login')}
        </a>
      </p>
    </form>
  )
}
